import React from "react";
import {
  StyledContentSection,
  ContentBlockItemWrapper,
  Container,
  StyledContentBlock,
  HiddenContainer,
} from "../ContentBlockItem/_.styled";
import { StyledHeading, SectionHeadingForPrint } from "./_.styled";
import { quickAnswerLinkProcessor } from "./preProcessors/quickAnswerLinkProcessor";
import { drugConsultLinkProcessor } from "./preProcessors/drugConsultLinkProcessor";
import { ReferenceDialog } from "../ReferenceDialog";

class DetailsBlockItem extends React.PureComponent {
  state = { referenceDialog: null };
  contentSection = React.createRef();

  handleContentClick = (e) => {
    const isReferenceLink = e.target.classList.contains("modalid");

    if (!isReferenceLink) {
      return;
    }

    const { href } = e.target;
    const [, id] = href.split("#");

    const modal = this.contentSection.current.querySelector("#" + id);
    if (!modal) {
      return;
    }

    const title =
      modal.querySelector(".modal-title")?.textContent || "References";
    const content = modal.querySelector(".modal-data")?.textContent;

    const referenceDialog = { title, content };

    this.setState({ referenceDialog });
    e.preventDefault();
  };
  handleDialogClose = () => this.setState({ referenceDialog: null });

  render() {
    const {
      isDetailsSectionFetching,
      detailsContent,
      detailsSectionIndex,
      detailsSubSectionIndex,
      detailsToc,
      slugPubtype,
      slugTitle,
      detailsSectionCache,
      sectionMap,
      selectedReferences,
      drugConsultUrlTemplate,
      dispatch,
      telemetryData,
    } = this.props;

    const processQuickAnswerLink = quickAnswerLinkProcessor({
      slugPubtype,
      slugTitle,
    });
    const processDrugConsultLink = drugConsultLinkProcessor({
      dispatch,
      drugConsultUrlTemplate,
      telemetryData,
    });
    const preProcessors = [processQuickAnswerLink, processDrugConsultLink];

    const {
      title: activeSubsectionTitle,
      sectionId: activeSubsectionId,
      reference: activeSubsectionReference,
    } =
      detailsToc?.[detailsSectionIndex]?.subSection?.[detailsSubSectionIndex] ??
      {};

    const getSubsectionsSelectedForPrint = (subsections) => {
      return subsections?.filter((subsection) => {
        const subsectionReference = subsection.reference;
        return (
          subsectionReference === activeSubsectionReference ||
          selectedReferences.includes(subsectionReference)
        );
      });
    };

    const renderSingleDetailsSubsection = (
      subsectionId,
      subsectionTitle,
      subsectionContent,
      shouldHideActiveSubsectionForPrint
    ) => {
      return (
        <ContentBlockItemWrapper
          id={subsectionId}
          data-testid={`${subsectionId}-wrapper`}
          printHide={!!shouldHideActiveSubsectionForPrint}
          onClick={this.handleContentClick}
        >
          <StyledHeading
            color="headingprimary"
            element="h2"
            fontFamily="secondary"
          >
            {subsectionTitle}
          </StyledHeading>
          {/* TODO: need to build details section menu */}
          {subsectionContent && (
            <StyledContentBlock
              data-testid={`${subsectionId}-content`}
              children={subsectionContent}
              preProcessors={preProcessors}
            />
          )}
        </ContentBlockItemWrapper>
      );
    };

    const renderPrintSubsections = (
      subsectionsSelectedForPrint,
      shouldHideActiveSubsectionForPrint
    ) => {
      return subsectionsSelectedForPrint.map((subsection) => {
        const isActiveSubsection = subsection.sectionId === activeSubsectionId;

        const { sectionIndex, subSectionIndex } = sectionMap[
          subsection.sectionId
        ];
        const subsectionContent =
          detailsSectionCache[sectionIndex]?.[subSectionIndex]?.data;

        return isActiveSubsection ? (
          <Container key={activeSubsectionId}>
            {renderSingleDetailsSubsection(
              activeSubsectionId,
              activeSubsectionTitle,
              detailsContent,
              shouldHideActiveSubsectionForPrint
            )}
          </Container>
        ) : (
          <HiddenContainer
            key={subsection.sectionId}
            data-testid="hidden-container"
          >
            {renderSingleDetailsSubsection(
              subsection.sectionId,
              subsection.title,
              subsectionContent
            )}
          </HiddenContainer>
        );
      });
    };

    const renderPrintSections = () => {
      const shouldHideActiveSubsectionForPrint = !selectedReferences.includes(
        activeSubsectionReference
      );

      return detailsToc.map((section) => {
        const subsectionsSelectedForPrint = getSubsectionsSelectedForPrint(
          section.subSection
        );

        if (!subsectionsSelectedForPrint.length) {
          return <></>;
        }

        const shouldHideSectionHeadingForPrint =
          activeSectionTitle === section.title &&
          shouldHideActiveSubsectionForPrint &&
          subsectionsSelectedForPrint.length === 1;

        return (
          <>
            <SectionHeadingForPrint
              element="h2"
              color="secondary"
              fontFamily="secondary"
            >
              {!shouldHideSectionHeadingForPrint && section.title}
            </SectionHeadingForPrint>
            {renderPrintSubsections(
              subsectionsSelectedForPrint,
              shouldHideActiveSubsectionForPrint
            )}
          </>
        );
      });
    };

    const onlyActiveSubsectionSelectedForPrint =
      selectedReferences.length === 1 &&
      selectedReferences.includes(activeSubsectionReference);

    const activeSectionTitle = detailsToc?.[detailsSectionIndex]?.title;

    const shouldonlyRenderActiveSubsectionSelectedForPrint =
      detailsContent &&
      !isDetailsSectionFetching &&
      (!selectedReferences.length || onlyActiveSubsectionSelectedForPrint);

    const { referenceDialog } = this.state;
    return (
      <StyledContentSection
        data-testid="details-section-wrapper"
        ref={this.contentSection}
      >
        <ReferenceDialog
          isOpen={!!referenceDialog}
          onClose={this.handleDialogClose}
          title={referenceDialog?.title}
          content={referenceDialog?.content}
        />
        {shouldonlyRenderActiveSubsectionSelectedForPrint ? (
          <>
            <SectionHeadingForPrint
              element="h2"
              color="secondary"
              fontFamily="secondary"
            >
              {activeSectionTitle}
            </SectionHeadingForPrint>
            <Container key={activeSubsectionId}>
              {renderSingleDetailsSubsection(
                activeSubsectionId,
                activeSubsectionTitle,
                detailsContent
              )}
            </Container>
          </>
        ) : (
          renderPrintSections()
        )}
      </StyledContentSection>
    );
  }
}

DetailsBlockItem.displayName = "DetailsBlockItem";

export default DetailsBlockItem;
